import { useForm } from "@inertiajs/react";
import { refund_bizminer_admin_organization_credit_path } from "@/routes";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { format } from "date-fns";
import { Label } from "@/components/ui/label.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Transaction } from "@/bizminer/Admin/Organizations/_components/CreditTransactionsTable.tsx";

interface RefundModalProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction;
  organizationId: number;
}

export const RefundModal = ({
  open,
  onClose,
  transaction,
  organizationId,
}: RefundModalProps) => {
  const { data, setData, post, processing, errors, reset } = useForm({
    reason: "",
  });

  const handleSubmit = () => {
    if (!data.reason.trim()) {
      return;
    }

    post(
      refund_bizminer_admin_organization_credit_path(
        organizationId,
        transaction.id,
      ),
      {
        preserveScroll: true,
        onSuccess: () => {
          reset();
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] text-foreground">
        <DialogHeader>
          <DialogTitle>Refund Transaction</DialogTitle>
          <DialogDescription>
            Refund {Math.abs(transaction.amount)} credits from this transaction
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount:</span>
              <span className="font-medium">
                {Math.abs(transaction.amount)} credits
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Category:</span>
              <span className="font-medium capitalize">
                {transaction.category}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Date:</span>
              <span className="font-medium">
                {format(new Date(transaction.created_at), "MMM d, yyyy")}
              </span>
            </div>
          </div>

          <div>
            <Label htmlFor="reason">Refund Reason *</Label>
            <Textarea
              id="reason"
              placeholder="e.g., Analysis failed to complete"
              value={data.reason}
              onChange={(e) => setData("reason", e.target.value)}
              className="mt-1"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {data.reason.length}/500 characters
            </p>
            {errors.reason && (
              <p className="text-sm text-destructive mt-1">{errors.reason}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={processing || !data.reason.trim()}
            >
              {processing ? "Processing..." : "Confirm Refund"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
